// FRECUENCIA_PUNTAJE: Mapea el id de frecuencia de medición a su puntaje correspondiente.
export const FRECUENCIA_PUNTAJE = {
  1: 4,
  2: 3,
  3: 2,
  4: 1,
  5: 0,
}

// RESULTADOS: Relaciona el id de resultado con su descripción textual.
export const RESULTADOS = {
  1: 'Excelente',
  2: 'Sobresaliente',
  3: 'Satisfactorio',
  4: 'Insuficiente',
  5: 'No satisfactorio',
}

/**
 * Calculates the total score by summing the scores of all evaluation items.
 *
 * @param {Array} evaluationItems - An array of evaluation items, each containing a `idFrecuencia` property.
 * @returns {number} The total score of all evaluation items.
 */
export const getPuntajeTotal = (evaluationItems) => {
  return evaluationItems.reduce((total, item) => total + (FRECUENCIA_PUNTAJE[item.idFrecuencia] ?? 0), 0)
}

/**
 * Calculates the evaluation result ID based on the total score (`puntajeTotal`).
 *
 * @readonly
 * @type {number}
 * @returns {number} The result ID:
 *   - 5 if `puntajeTotal` is 38 or less,
 *   - 4 if `puntajeTotal` is between 39 and 44,
 *   - 3 if `puntajeTotal` is between 45 and 51,
 *   - 2 if `puntajeTotal` is between 52 and 57,
 *   - 1 if `puntajeTotal` is 58 or more.
 */

/**
 * getIdResultado:
 * Determina el id del resultado (1 a 5) según el puntaje total y el tipo de evaluación.
 * La lógica varía dependiendo del tipo de evaluación para reflejar correctamente el desempeño.
 */
export const getIdResultado = (puntajeTotal, tipoEvaluacion) => {
  if (tipoEvaluacion === 'desempeño-institucional') {
    if (puntajeTotal > 42) return 1 // Excelente
    if (puntajeTotal >= 36 && puntajeTotal <= 42) return 2 // Sobresaliente
    if (puntajeTotal >= 31 && puntajeTotal <= 35) return 3 // Satisfactorio
    if (puntajeTotal >= 27 && puntajeTotal <= 30) return 4 // Insuficiente
    return 5 // No satisfactorio
  }
  if (tipoEvaluacion === 'rendimiento-profesionalismo') {
    if (puntajeTotal > 60) return 1
    if (puntajeTotal >= 48 && puntajeTotal <= 60) return 2
    if (puntajeTotal >= 42 && puntajeTotal <= 47) return 3
    if (puntajeTotal >= 36 && puntajeTotal <= 41) return 4
    return 5
  }
  if (tipoEvaluacion === 'respeto-valores-principios') {
    if (puntajeTotal > 64) return 1
    if (puntajeTotal >= 52 && puntajeTotal <= 64) return 2
    if (puntajeTotal >= 45 && puntajeTotal <= 51) return 3
    if (puntajeTotal >= 39 && puntajeTotal <= 44) return 4
    return 5
  }
  // Lógica default para otros casos o tipos desconocidos
  if (puntajeTotal <= 38) return 5
  if (puntajeTotal < 45) return 4
  if (puntajeTotal < 52) return 3
  if (puntajeTotal < 58) return 2
  return 1
}

/**
 * Calculates the calification (grade) based on the total score (`puntajeTotal`).
 *
 * The grading scale is as follows:
 * - Returns '0' if `puntajeTotal` is 0.
 * - Returns '5' if `puntajeTotal` is less than 39.
 * - Returns '6' if `puntajeTotal` is less than 45.
 * - Returns '7' if `puntajeTotal` is less than 52.
 * - Returns '8' if `puntajeTotal` is less than 58.
 * - Returns '9' if `puntajeTotal` is less than 62.
 * - Returns '10' otherwise.
 *
 * @returns {string} The calification as a string from '0' to '10'.
 */

/**
 * getCalification:
 * Calcula la calificación (del 0 al 10) según el puntaje total y el tipo de evaluación.
 * Cada tipo de evaluación tiene su propia escala de calificación.
 */
export const getCalification = (puntajeTotal, tipoEvaluacion) => {
  if (tipoEvaluacion === 'desempeño-institucional') {
    if (puntajeTotal > 42) return '10'
    if (puntajeTotal >= 40 && puntajeTotal <= 42) return '9'
    if (puntajeTotal >= 36 && puntajeTotal <= 39) return '8'
    if (puntajeTotal >= 31 && puntajeTotal <= 35) return '7'
    if (puntajeTotal >= 27 && puntajeTotal <= 30) return '6'
    if (puntajeTotal >= 13 && puntajeTotal <= 26) return '5'
    if (puntajeTotal < 13 && puntajeTotal >= 2) return '0'
    return '0'
  }

  if (tipoEvaluacion === 'rendimiento-profesionalismo') {
    if (puntajeTotal > 60) return '10'
    if (puntajeTotal >= 54 && puntajeTotal <= 60) return '9'
    if (puntajeTotal >= 48 && puntajeTotal <= 53) return '8'
    if (puntajeTotal >= 42 && puntajeTotal <= 47) return '7'
    if (puntajeTotal >= 36 && puntajeTotal <= 41) return '6'
    if (puntajeTotal >= 14 && puntajeTotal <= 35) return '5'
    if (puntajeTotal < 13 && puntajeTotal >= 2) return '0'
    return '0'
  }

  if (tipoEvaluacion === 'respeto-valores-principios') {
    if (puntajeTotal > 64) return '10'
    if (puntajeTotal >= 58 && puntajeTotal <= 64) return '9'
    if (puntajeTotal >= 52 && puntajeTotal <= 57) return '8'
    if (puntajeTotal >= 45 && puntajeTotal <= 51) return '7'
    if (puntajeTotal >= 39 && puntajeTotal <= 44) return '6'
    if (puntajeTotal >= 14 && puntajeTotal <= 38) return '5'
    if (puntajeTotal < 13 && puntajeTotal >= 2) return '0'
    return '0'
  }
  // Lógica default para otros casos o tipos desconocidos
  if (puntajeTotal === 0) return '0'
  if (puntajeTotal < 39) return '5'
  if (puntajeTotal < 45) return '6'
  if (puntajeTotal < 52) return '7'
  if (puntajeTotal < 58) return '8'
  if (puntajeTotal < 62) return '9'
  return '10'
}
